import type {InferInsertModel} from 'drizzle-orm';
import {sql} from 'drizzle-orm';
import {
  customType,
  boolean,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
  pgEnum,
  unique,
  uniqueIndex
} from 'drizzle-orm/pg-core';
import type {AnyPgColumn} from 'drizzle-orm/pg-core';

export const scopesEnum = pgEnum('scopes', ['site', 'tenant']);
export const roleActionsEnum = pgEnum('role_actions', ['grant', 'revoke', 'modify', 'expire']);

// Processing status for asynchronous file ingestion
export const processingStatusEnum = pgEnum('processing_status', ['pending', 'processing', 'completed', 'failed']);

export const knowledgeBases = pgTable(
  'knowledge_bases',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    name: varchar('name', {length: 255}).notNull(),
    avatar: text('avatar'),
    description: text('description'),
    tenantId: uuid('tenant_id').notNull(),
    sourceCount: integer('source_count').notNull().default(0),
    embeddingModelId: varchar('embedding_model_id', {length: 255}).notNull(),
    createdBy: uuid('created_by').notNull(),
    createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow(),
    updatedBy: uuid('updated_by').notNull(),
    updatedAt: timestamp('updated_at', {withTimezone: true}).notNull().defaultNow()
  },
  (t) => [index('kb_idx_tenantId').on(t.tenantId)]
);

export type KnowledgeBase = InferInsertModel<typeof knowledgeBases>;

// Files, URLs, etc.
export const sources = pgTable(
  'sources',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id').notNull(),
    kbId: uuid('kb_id').notNull(),
    name: varchar('name', {length: 255}).notNull(),
    uri: text('uri'),
    numDocuments: integer('num_documents').default(0),
    type: varchar('type', {length: 32}).notNull(),
    createdBy: uuid('created_by').notNull(),
    createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow(),
    updatedBy: uuid('updated_by').notNull(),
    updatedAt: timestamp('updated_at', {withTimezone: true}).notNull().defaultNow(),
    processingStatus: processingStatusEnum('processing_status').notNull().default('pending'),
    processingError: text('processing_error'),
    markdownKey: varchar('markdown_key', {length: 512})
  },
  (t) => [
    index('src_idx_kbId').on(t.kbId),
    index('src_idx_tenantId').on(t.tenantId),
    index('src_idx_sourceId').on(t.id)
  ]
);
export type Source = InferInsertModel<typeof sources>;

export const vectorWithoutDimensions = customType<{data: number[]}>({
  dataType() {
    return 'vector';
  }
});

export const documents = pgTable(
  'documents',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    content: text('content'),
    metadata: jsonb('metadata'),
    vector: vectorWithoutDimensions()
  },
  () => [
    index('doc_idx_kbId').using('gin', sql`(metadata->'kbId')`),
    index('doc_idx_tenantId').using('gin', sql`(metadata->'tenantId')`),
    index('doc_idx_sourceId').using('gin', sql`(metadata->'sourceId')`)
  ]
);
export type Document = InferInsertModel<typeof documents>;

export const tenants = pgTable('tenants', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', {length: 100}).notNull().unique(),
  logo: text('logo'),
  createdBy: uuid('created_by').notNull(),
  createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow(),
  updatedBy: uuid('updated_by').notNull(),
  updatedAt: timestamp('updated_at', {withTimezone: true}).notNull().defaultNow()
});

export type Tenant = InferInsertModel<typeof tenants>;

// System Prompts
export const chatConversations = pgTable('chat_conversations', {
  id: uuid('id').primaryKey().defaultRandom(),
  title: varchar('title', {length: 255}),
  userId: uuid('user_id').notNull(),
  tenantId: uuid('tenant_id').notNull(),
  assistantId: uuid('assistant_id').references(() => assistants.id),
  createdBy: uuid('created_by').notNull(),
  createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow(),
  updatedBy: uuid('updated_by').notNull(),
  updatedAt: timestamp('updated_at', {withTimezone: true}).notNull().defaultNow()
});
export type ChatConversation = InferInsertModel<typeof chatConversations>;

export const chatMessageRoleEnum = pgEnum('chat_message_role', ['user', 'assistant']);

/**
 * Durable display history for the chat UI. We persist user prompts and the
 * final assistant text response per turn so the UI can show stable history
 * even when the underlying LangGraph checkpoint is summarized/compressed.
 * Reasoning blocks and tool calls are kept only in the checkpoint state.
 */
export const chatMessages = pgTable(
  'chat_messages',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    conversationId: uuid('conversation_id')
      .notNull()
      .references(() => chatConversations.id, {onDelete: 'cascade'}),
    tenantId: uuid('tenant_id').notNull(),
    userId: uuid('user_id').notNull(),
    role: chatMessageRoleEnum('role').notNull(),
    content: text('content').notNull(),
    sequence: integer('sequence').notNull(),
    tokenCount: integer('token_count'),
    sources: jsonb('sources'),
    // Tool invocations that produced this turn's answer (assistant rows only).
    // Each entry: { toolName, input, output, sequence } where sequence is the
    // intra-turn order. Hydrating these on the next turn preserves the RAG
    // continuity that prior PostgresSaver-based architectures got "for free":
    // the model sees what was searched and what came back, so a follow-up
    // question can build on the prior retrieval instead of re-searching.
    toolCalls: jsonb('tool_calls'),
    createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow()
  },
  (t) => [
    index('cm_idx_conversationId_sequence').on(t.conversationId, t.sequence),
    index('cm_idx_tenantId').on(t.tenantId)
  ]
);

export type ChatMessage = InferInsertModel<typeof chatMessages>;

export const chatAttachmentKindEnum = pgEnum('chat_attachment_kind', ['image', 'document']);

/**
 * Attachments uploaded by users in chat. Created at upload time with a null
 * messageId, then bound to a chat_messages row when the user sends. Orphaned
 * rows (no messageId after a grace period) are cleaned up by a sweeper.
 */
export const chatAttachments = pgTable(
  'chat_attachments',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id').notNull(),
    userId: uuid('user_id').notNull(),
    conversationId: uuid('conversation_id').references(() => chatConversations.id, {onDelete: 'cascade'}),
    messageId: uuid('message_id').references(() => chatMessages.id, {onDelete: 'cascade'}),
    filename: varchar('filename', {length: 255}).notNull(),
    mimeType: varchar('mime_type', {length: 255}).notNull(),
    sizeBytes: integer('size_bytes').notNull(),
    storageKey: varchar('storage_key', {length: 1024}).notNull(),
    // Storage key for the extracted-markdown rendition of a document
    // attachment. Populated at upload time for PDFs / Office docs so the
    // chat completion path can inline ~100 KB of text instead of streaming
    // a multi-MB binary through the LLM call. Null for images and for
    // documents that failed extraction; null kinds get a [content not
    // available] marker in the prompt instead.
    extractedMarkdownKey: varchar('extracted_markdown_key', {length: 1024}),
    kind: chatAttachmentKindEnum('kind').notNull(),
    width: integer('width'),
    height: integer('height'),
    createdBy: uuid('created_by').notNull(),
    createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow(),
    updatedBy: uuid('updated_by').notNull(),
    updatedAt: timestamp('updated_at', {withTimezone: true}).notNull().defaultNow()
  },
  (t) => [
    index('ca_idx_tenantId').on(t.tenantId),
    index('ca_idx_conversationId').on(t.conversationId),
    index('ca_idx_messageId').on(t.messageId),
    index('ca_idx_unbound_createdAt')
      .on(t.createdAt)
      .where(sql`${t.messageId} is null`)
  ]
);

export type ChatAttachment = InferInsertModel<typeof chatAttachments>;

export const users = pgTable('users', {
  id: uuid('id').primaryKey(),
  email: varchar('email', {length: 320}).notNull().unique(),
  fullName: varchar('full_name', {length: 255}).notNull(),
  isActive: boolean('is_active').notNull().default(true),
  createdBy: varchar('created_by', {length: 255}).notNull(),
  createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow()
});

export type Users = InferInsertModel<typeof users>;

export const roleTypes = pgTable('role_types', {
  id: uuid('id').primaryKey().defaultRandom(),
  role: text('role').notNull(),
  scope: scopesEnum('scope').notNull()
});

export type RoleTypes = InferInsertModel<typeof roleTypes>;

// User Tenant Roles
export const roles = pgTable('roles', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id')
    .notNull()
    .references(() => users.id),
  roleTypeId: uuid('role_type_id')
    .notNull()
    .references(() => roleTypes.id),
  scopeId: uuid('scope_id') // refers to the scoped entity (tenantId, etc). Nullable for site-wide roles.
});

export const invites = pgTable('invites', {
  id: uuid('id').primaryKey().defaultRandom(),
  recipientEmail: varchar('recipient_email', {length: 320}).notNull(),
  roleTypeId: uuid('role_type_id'),
  scopeId: uuid('scope_id'),
  acceptedAt: timestamp('accepted_at', {withTimezone: true}),
  withdrawnAt: timestamp('withdrawn_at', {withTimezone: true}),
  invitedAt: timestamp('invited_at', {withTimezone: true}).notNull().defaultNow(),
  invitedBy: uuid('invited_by').notNull()
});

export type Roles = InferInsertModel<typeof roles>;

// Tools an assistant has enabled. Each entry references a tool by its
// catalog id (e.g. 'WebSearchTool', 'KnowledgeBaseDocumentSearchTool') and
// may carry tool-specific config. An empty array means the assistant has
// no tools enabled (general chat keeps the full built-in catalog — that
// default lives in chat-fn, not here).
export type AssistantEnabledTool = {
  toolId: string;
  config?: Record<string, unknown>;
};

export const assistants = pgTable(
  'assistants',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    name: varchar('name', {length: 100}).notNull(),
    description: text('description'),
    avatar: text('avatar'),
    prompt: text('prompt').notNull(),
    userInstructions: text('user_instructions'),
    firstMessage: text('first_message'),
    tenantId: uuid('tenant_id').notNull(),
    enabledTools: jsonb('enabled_tools').$type<AssistantEnabledTool[]>().notNull().default([]),
    createdBy: uuid('created_by').notNull(),
    createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow(),
    updatedBy: uuid('updated_by').notNull(),
    updatedAt: timestamp('updated_at', {withTimezone: true}).notNull().defaultNow()
  },
  (t) => [unique('assistants_tenant_id_name_unique').on(t.tenantId, t.name)]
);

export type Assistant = InferInsertModel<typeof assistants>;

export const assistantKnowledgeBases = pgTable(
  'assistant_knowledge_bases',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    assistantId: uuid('assistant_id')
      .notNull()
      .references(() => assistants.id, {onDelete: 'cascade'}),
    knowledgeBaseId: uuid('knowledge_base_id')
      .notNull()
      .references(() => knowledgeBases.id, {onDelete: 'cascade'}),
    tenantId: uuid('tenant_id').notNull(),
    createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow()
  },
  (t) => [
    index('akb_idx_assistantId').on(t.assistantId),
    index('akb_idx_knowledgeBaseId').on(t.knowledgeBaseId),
    index('akb_idx_tenantId').on(t.tenantId),
    unique('akb_unique_assistant_kb').on(t.assistantId, t.knowledgeBaseId)
  ]
);

export type AssistantKnowledgeBase = InferInsertModel<typeof assistantKnowledgeBases>;

// Cached metadata about a tool discovered on an external MCP server. We
// store enough to render the catalog UI without re-connecting to the
// server on every page load. The live JSON Schema is re-fetched at
// chat-fn request time so an agent always sees the server's current
// tool surface.
export type DiscoveredMcpTool = {
  name: string;
  title?: string;
  description: string;
};

// Customer-registered MCP servers. Each tenant can register multiple;
// each carries an opaque auth header (encrypted at rest with
// MCP_AUTH_ENCRYPTION_KEY) and a cached snapshot of the tools the
// server exposed at last discovery. Per-request, chat-fn instantiates
// an MCP client per enabled row and binds the discovered tools
// alongside the embedded built-ins.
export const tenantMcpServers = pgTable(
  'tenant_mcp_servers',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id').notNull(),
    displayName: varchar('display_name', {length: 100}).notNull(),
    url: text('url').notNull(),
    transport: varchar('transport', {length: 32}).notNull().default('http_stream'),
    // Ciphertext of the literal Authorization header value (e.g. the
    // full "Bearer abc..." string). Encrypted with AES-256-GCM; the
    // ciphertext, IV, and auth tag are concatenated as base64. NULL =
    // no auth header (public MCP server).
    authHeaderCiphertext: text('auth_header_ciphertext'),
    enabled: boolean('enabled').notNull().default(true),
    discoveredTools: jsonb('discovered_tools').$type<DiscoveredMcpTool[]>().notNull().default([]),
    lastDiscoveryAt: timestamp('last_discovery_at', {withTimezone: true}),
    lastDiscoveryError: text('last_discovery_error'),
    createdBy: uuid('created_by').notNull(),
    createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow(),
    updatedBy: uuid('updated_by').notNull(),
    updatedAt: timestamp('updated_at', {withTimezone: true}).notNull().defaultNow()
  },
  (t) => [
    index('tms_idx_tenantId').on(t.tenantId),
    unique('tms_tenant_id_display_name_unique').on(t.tenantId, t.displayName)
  ]
);

export type TenantMcpServer = typeof tenantMcpServers.$inferSelect;
export type TenantMcpServerInsert = InferInsertModel<typeof tenantMcpServers>;

// Application Configuration
export const appConfigs = pgTable(
  'app_configs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    key: varchar('key', {length: 255}).notNull(),
    value: text('value').notNull(),
    isCurrent: boolean('is_current').notNull().default(true),
    replaceId: uuid('replace_id').references((): AnyPgColumn => appConfigs.id),
    createdBy: uuid('created_by').notNull(),
    createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow(),
    updatedBy: uuid('updated_by').notNull(),
    updatedAt: timestamp('updated_at', {withTimezone: true}).notNull().defaultNow()
  },
  (t) => [
    index('app_configs_idx_key').on(t.key),
    index('app_configs_idx_is_current').on(t.isCurrent),
    uniqueIndex('app_configs_unique_current_key')
      .on(t.key)
      .where(sql`${t.isCurrent} = true`)
  ]
);

export type AppConfig = InferInsertModel<typeof appConfigs>;
// Token usage tracking for LLM operations
export const operationTypeEnum = pgEnum('operation_type', [
  'chat',
  'title',
  'summarization',
  'keywords',
  'image_description'
]);

export const tokenUsage = pgTable(
  'token_usage',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    conversationId: uuid('conversation_id').references(() => chatConversations.id),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id),
    model: varchar('model', {length: 255}).notNull(),
    operationType: operationTypeEnum('operation_type').notNull(),
    inputTokens: integer('input_tokens').notNull(),
    outputTokens: integer('output_tokens').notNull(),
    totalTokens: integer('total_tokens').notNull(),
    createdAt: timestamp('created_at', {withTimezone: true}).notNull().defaultNow()
  },
  (t) => [
    index('tu_idx_conversationId').on(t.conversationId),
    index('tu_idx_userId').on(t.userId),
    index('tu_idx_tenantId').on(t.tenantId),
    index('tu_idx_createdAt').on(t.createdAt),
    index('tu_idx_operationType').on(t.operationType)
  ]
);

export type TokenUsage = InferInsertModel<typeof tokenUsage>;
